/**
 * T111-T115: React Hook for Real-time Installation Progress
 *
 * Connects to SSE endpoint to receive live installation updates.
 * Handles connection, reconnection, fallback polling, and timeout.
 *
 * Events received:
 * - connected: Initial connection established
 * - progress_update: Installation progress (0-100%)
 * - status_change: Instance status changed
 * - log_message: Detailed installation step
 * - complete: Installation finished successfully
 * - error: Installation failed
 * - ping: Keepalive (ignored)
 *
 * Usage:
 * ```tsx
 * const { events, progress, status, error, isConnected } = useInstallationEvents(instanceId);
 * ```
 */

import { useState, useEffect, useCallback, useRef } from 'react';

export interface InstallationEvent {
  type: 'connected' | 'progress_update' | 'status_change' | 'log_message' | 'complete' | 'error' | 'ping';
  timestamp: string;
  data?: any;
}

export interface InstallationProgress {
  progress: number; // 0-100
  status: string; // installing, verifying, active, failed
  message: string;
}

export interface UseInstallationEventsResult {
  events: InstallationEvent[];
  progress: number;
  status: string;
  message: string;
  error: string | null;
  isConnected: boolean;
  isComplete: boolean;
  reconnect: () => void;
}

const MAX_EVENTS = 100; // Limit event history to prevent memory leaks
const POLLING_INTERVAL = 10000; // 10 seconds (T114: fallback polling)
const CONNECTION_TIMEOUT = 600000; // 10 minutes (T115: connection timeout)

export function useInstallationEvents(instanceId: string): UseInstallationEventsResult {
  const [events, setEvents] = useState<InstallationEvent[]>([]);
  const [progress, setProgress] = useState<number>(0);
  const [status, setStatus] = useState<string>('pending');
  const [message, setMessage] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [isComplete, setIsComplete] = useState<boolean>(false);

  const eventSourceRef = useRef<EventSource | null>(null);
  const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const connectionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttemptsRef = useRef<number>(0);

  const addEvent = useCallback((event: InstallationEvent) => {
    setEvents((prev) => {
      const updated = [...prev, event];
      // Limit event history to prevent memory leaks
      return updated.slice(-MAX_EVENTS);
    });
  }, []);

  // T114: Polling fallback for browsers without EventSource support
  const startPolling = useCallback(async () => {
    if (!window.EventSource) {
      console.warn('EventSource not supported, using polling fallback');

      const poll = async () => {
        try {
          // Fetch current installation status
          const response = await fetch(`/api/client/instances/${instanceId}`, {
            credentials: 'include',
          });

          if (response.ok) {
            const instance = await response.json();
            setProgress(instance.installation_progress || 0);
            setStatus(instance.status || 'pending');
            setMessage(instance.installation_message || '');

            if (instance.status === 'active') {
              setIsComplete(true);
              return; // Stop polling
            } else if (instance.status === 'error') {
              setError(instance.installation_message || 'Installation failed');
              return; // Stop polling
            }
          }
        } catch (err) {
          console.error('Polling error:', err);
        }

        // Schedule next poll
        pollingTimeoutRef.current = setTimeout(poll, POLLING_INTERVAL);
      };

      poll();
    }
  }, [instanceId]);

  // T112: Connect to SSE endpoint
  const connectSSE = useCallback(() => {
    if (!window.EventSource) {
      startPolling();
      return;
    }

    // Close existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    try {
      const url = `/api/client/instances/${instanceId}/install-events`;
      const eventSource = new EventSource(url, { withCredentials: true });
      eventSourceRef.current = eventSource;

      // T115: Connection timeout (10 minutes)
      connectionTimeoutRef.current = setTimeout(() => {
        console.warn('SSE connection timeout (10 minutes), closing connection');
        eventSource.close();
        setError('Installation timeout - please check troubleshooting guide');
        setIsConnected(false);
      }, CONNECTION_TIMEOUT);

      // Connection opened
      eventSource.onopen = () => {
        console.log('SSE connection opened');
        setIsConnected(true);
        reconnectAttemptsRef.current = 0; // Reset reconnect counter
      };

      // T113: Parse and handle different event types
      eventSource.addEventListener('connected', (e: MessageEvent) => {
        const data = JSON.parse(e.data);
        addEvent({ type: 'connected', timestamp: data.timestamp, data });
        console.log('Connected to installation event stream');
      });

      eventSource.addEventListener('progress_update', (e: MessageEvent) => {
        const data = JSON.parse(e.data);
        setProgress(data.progress);
        setStatus(data.status);
        setMessage(data.message);

        addEvent({ type: 'progress_update', timestamp: data.timestamp, data });
      });

      eventSource.addEventListener('status_change', (e: MessageEvent) => {
        const data = JSON.parse(e.data);
        setStatus(data.new_status);
        addEvent({ type: 'status_change', timestamp: data.timestamp, data });
      });

      eventSource.addEventListener('log_message', (e: MessageEvent) => {
        const data = JSON.parse(e.data);
        addEvent({ type: 'log_message', timestamp: data.timestamp, data });
      });

      eventSource.addEventListener('complete', (e: MessageEvent) => {
        const data = JSON.parse(e.data);
        setIsComplete(true);
        setProgress(100);
        setStatus('active');
        setMessage('Installation completed successfully');
        addEvent({ type: 'complete', timestamp: data.timestamp, data });

        // Close connection
        eventSource.close();
        setIsConnected(false);

        // Clear timeout
        if (connectionTimeoutRef.current) {
          clearTimeout(connectionTimeoutRef.current);
        }
      });

      eventSource.addEventListener('error', (e: MessageEvent) => {
        const data = JSON.parse(e.data);
        setError(data.message || 'Installation failed');
        setStatus('failed');
        addEvent({ type: 'error', timestamp: data.timestamp, data });

        // Close connection
        eventSource.close();
        setIsConnected(false);

        // Clear timeout
        if (connectionTimeoutRef.current) {
          clearTimeout(connectionTimeoutRef.current);
        }
      });

      // Ignore ping events (keepalive)
      eventSource.addEventListener('ping', () => {
        // Silent keepalive, no action needed
      });

      // Connection error
      eventSource.onerror = (err) => {
        console.error('SSE connection error:', err);
        setIsConnected(false);

        // Retry connection (max 3 attempts)
        if (reconnectAttemptsRef.current < 3) {
          reconnectAttemptsRef.current += 1;
          console.log(`Reconnecting (attempt ${reconnectAttemptsRef.current}/3)...`);
          setTimeout(connectSSE, 2000 * reconnectAttemptsRef.current); // Exponential backoff
        } else {
          console.error('Max reconnection attempts reached, falling back to polling');
          eventSource.close();
          startPolling();
        }
      };
    } catch (err) {
      console.error('Error creating EventSource:', err);
      setError('Failed to connect to installation stream');
      startPolling(); // Fallback to polling
    }
  }, [instanceId, addEvent, startPolling]);

  const reconnect = useCallback(() => {
    reconnectAttemptsRef.current = 0;
    setError(null);
    setIsConnected(false);
    connectSSE();
  }, [connectSSE]);

  // Connect on mount, disconnect on unmount
  useEffect(() => {
    connectSSE();

    return () => {
      // Cleanup on unmount
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
      }
      if (connectionTimeoutRef.current) {
        clearTimeout(connectionTimeoutRef.current);
      }
    };
  }, [connectSSE]);

  return {
    events,
    progress,
    status,
    message,
    error,
    isConnected,
    isComplete,
    reconnect,
  };
}
